import React from 'react';

import { Badge } from '../ui/badge';

import { RefreshCw, FileText, ShieldCheck } from 'lucide-react';

import { toast } from 'sonner';
import { API } from './dosHelpers';

export function RefreshHistoryWidget({ jobs, onRefresh }) {
  const downloadAudit = (jobId) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    // Stream le PDF via fetch + Authorization (le simple <a href> ne porte
    // pas le Bearer). On le materialise ensuite en blob telechargeable.
    fetch(`${API}/domium/admin/refresh-cache-all/history/${jobId}/audit-pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          throw new Error(`HTTP ${r.status} ${txt}`);
        }
        const auditNumber = r.headers.get('X-Audit-Number') || jobId.slice(0, 8);
        const tsaSerial = r.headers.get('X-TSA-Serial');
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Audit_RefreshCacheAll_${auditNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        const tsaMsg = tsaSerial ? ` (horodaté TSA #${tsaSerial})` : ' (sans horodatage TSA)';
        toast.success(`Rapport d'audit ${auditNumber} téléchargé${tsaMsg}`);
      })
      .catch((err) => toast.error(`Téléchargement impossible : ${err.message}`));
  };

  const downloadTsr = (jobId) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    fetch(`${API}/domium/admin/refresh-cache-all/history/${jobId}/audit-pdf/tsr`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (r.status === 409) {
          toast.warning('Cet audit n\'a pas de TSA. Re-téléchargez d\'abord le PDF audit.');
          return;
        }
        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          throw new Error(`HTTP ${r.status} ${txt}`);
        }
        const auditNumber = r.headers.get('X-Audit-Number') || jobId.slice(0, 8);
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Audit_RefreshCacheAll_${auditNumber}.tsr`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Token TSA ${auditNumber} téléchargé`);
      })
      .catch((err) => toast.error(`TSR indisponible : ${err.message}`));
  };

  if (!jobs?.length) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-4 text-sm text-amber-900" data-testid="domium-refresh-history-empty">
        Aucun job de régénération globale enregistré pour l'instant.
      </div>
    );
  }

  // Durée max pour échelle du mini-graphe (en secondes)
  const maxDuration = jobs.reduce((m, j) => Math.max(m, j.duration_s || 0), 1);
  const fmtDuration = (s) => {
    if (!s && s !== 0) return '—';
    if (s < 60) return `${s.toFixed(1)}s`;
    const mn = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return `${mn}min ${sec}s`;
  };
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-white shadow-sm" data-testid="domium-refresh-history-widget">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-amber-100 bg-amber-50/70">
        <div className="text-sm font-bold text-amber-900 flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-amber-700" />
          Historique « Régénérer tout » — {jobs.length} derniers jobs
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="text-xs text-amber-800 hover:text-amber-900 underline underline-offset-2"
          data-testid="domium-refresh-history-reload"
        >
          Actualiser
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Démarré le</th>
              <th className="text-left px-3 py-2 font-semibold">Déclenché par</th>
              <th className="text-right px-3 py-2 font-semibold">Durée</th>
              <th className="text-right px-3 py-2 font-semibold">Périmètre</th>
              <th className="text-right px-3 py-2 font-semibold">Régénérés</th>
              <th className="text-right px-3 py-2 font-semibold">Erreurs</th>
              <th className="text-left px-3 py-2 font-semibold w-40">Durée (graph)</th>
              <th className="text-center px-3 py-2 font-semibold">Statut</th>
              <th className="text-center px-3 py-2 font-semibold">Audit</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const isRunning = !j.finished_at;
              const total = j.regenerated_total || 0;
              const ok = j.regenerated || 0;
              const ratio = total ? Math.round((ok / total) * 100) : 0;
              const widthPct = j.duration_s ? Math.max(2, Math.round((j.duration_s / maxDuration) * 100)) : 0;
              return (
                <tr key={j.id} className="border-t border-slate-100 hover:bg-amber-50/40" data-testid={`refresh-history-row-${j.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-700 whitespace-nowrap">{fmtDate(j.started_at)}</td>
                  <td className="px-3 py-2 text-slate-700 truncate" title={j.actor}>{j.actor}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-700">{fmtDuration(j.duration_s)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-700">{j.subscriptions_total ?? '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    <span className={`font-semibold ${ratio >= 95 ? 'text-emerald-700' : ratio >= 80 ? 'text-amber-700' : 'text-rose-700'}`}>
                      {ok}/{total}
                    </span>
                    {total > 0 && (
                      <span className="ml-1.5 text-[10px] text-slate-500">({ratio}%)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {j.regen_errors > 0 || j.r2_errors > 0 ? (
                      <span className="text-rose-700 font-semibold">{(j.regen_errors || 0) + (j.r2_errors || 0)}</span>
                    ) : (
                      <span className="text-emerald-700">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="h-2.5 w-full bg-slate-100 rounded overflow-hidden">
                      <div
                        className={`h-full ${j.regen_errors > 0 ? 'bg-rose-400' : 'bg-emerald-500'}`}
                        style={{ width: `${widthPct}%` }}
                        title={fmtDuration(j.duration_s)}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {isRunning ? (
                      <Badge className="bg-blue-600 text-white text-[10px] font-semibold">En cours</Badge>
                    ) : (j.regen_errors > 0 || j.r2_errors > 0) ? (
                      <Badge className="bg-amber-500 text-white text-[10px] font-semibold">Avec erreurs</Badge>
                    ) : (
                      <Badge className="bg-emerald-600 text-white text-[10px] font-semibold">Succès</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {isRunning ? (
                      <span className="text-[10px] text-slate-400 italic">—</span>
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => downloadAudit(j.id)}
                          title="Exporter ce job en PDF audit (hash SHA-256 + signature Super Admin + horodatage TSA)"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 hover:bg-indigo-50 px-2 py-1 rounded transition-colors"
                          data-testid={`refresh-history-audit-btn-${j.id}`}
                        >
                          <FileText className="h-3.5 w-3.5" />
                          PDF
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadTsr(j.id)}
                          title="Télécharger le token RFC 3161 (TSR) — preuve TSA vérifiable par openssl ts"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 px-1.5 py-1 rounded transition-colors"
                          data-testid={`refresh-history-tsr-btn-${j.id}`}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                          TSR
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 text-[10px] text-slate-500 bg-slate-50 border-t border-slate-100">
        Durée la plus longue ≈ {fmtDuration(maxDuration)}. Échelle du mini-graphe relative.
        Source : <code className="text-slate-700">domium_refresh_jobs</code>.
      </div>
    </div>
  );
}
